import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import importAsset from 'frontend-login/helpers/import-asset';
import { SSO_TYPES, type SSOType } from 'frontend-login/utils/constant-values';

import styles from './button-single-sign-on.module.css';

interface ButtonSingleSignOnSignature {
  Args: {
    isDisabled?: boolean;
    isLoading?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    onClick?: (event: MouseEvent) => void | unknown;
    /** type defines if it is a Google, Linkedin, Microsoft or SAML button */
    type: SSOType;
  };
  Element: HTMLButtonElement;
}

export default class ButtonSingleSignOn extends Component<ButtonSingleSignOnSignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedLabel() {
    if (this.args.type === SSO_TYPES.SAML) {
      return this.intl.t('login.single_sign_on');
    }

    return this.args.type;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get icon() {
    if (this.args.isDisabled) {
      return importAsset(`images/sso/${this.args.type}-disabled.svg`);
    }

    return importAsset(`images/sso/${this.args.type}.svg`);
  }

  @action
  onClick(event: MouseEvent): void {
    this.args.onClick?.(event);
  }

  <template>
    <button
      class={{concat
        "prism-button prism-button--secondary"
        " "
        styles.sso-button
        " "
        "flex items-center justify-start"
      }}
      disabled={{@isDisabled}}
      type="button"
      {{on "click" this.onClick}}
      ...attributes
    >
      <img alt="" class={{styles.icon}} src={{this.icon}} />
      <p class={{styles.label}}>{{this.formattedLabel}}</p>

      {{#if @isLoading}}
        {{! TODO This should be replaced when the spinner is implemented in prism }}
        <svg
          class={{styles.spinner}}
          height="25"
          viewBox="0 0 50 50"
          width="25"
        >
          <defs>
            <linearGradient
              id="loader__spinner-gradient"
              x1="0%"
              x2="100%"
              y1="0%"
              y2="0%"
            >
              <stop class={{styles.spinner-gradient-stop}} offset="0%"></stop>
              <stop class={{styles.spinner-gradient-stop}} offset="40%"></stop>
              <stop
                class={{styles.spinner-gradient-stop}}
                offset="60%"
                stop-opacity="0"
              ></stop>
            </linearGradient>
          </defs>
          <circle
            class={{styles.spinner-path}}
            cx="25"
            cy="25"
            fill="none"
            r="20"
            stroke-width="5"
          >
            <path d="M125,85 a60,60 0 1,0 -115,0"></path>
          </circle>
        </svg>
      {{/if}}
    </button>
  </template>
}
